/**
 * ThemeMascot — a mascot emoji that bounces around the viewport,
 * themed to the user's active playful theme.
 *
 * Mini-game: clicking the mascot increments a streak score.
 * Clicking anywhere else resets to 0. Every ×7 reverses direction.
 */

import { useRef, useCallback, useState, useEffect } from 'react';
import { usePlayful } from '@/styles/playful';
import { useThemeColors } from '@/styles/colors';
import { hexToRgba } from '@/styles/colors/theme-helpers';
import { typography } from '@/styles/design-tokens';
import { usePatrolAnimation, type Vec2 } from './usePatrolAnimation';
import { MASCOT_COMPONENTS } from './mascots';

const REACTIONS = [
  [
    { transform: 'translateY(-18px) scale(1.3)', offset: 0.3 },
    { transform: 'translateY(4px) scale(0.9)', offset: 0.6 },
    { transform: 'translateY(0) scale(1)', offset: 1 },
  ],
  [
    { transform: 'rotate(0deg) scale(1.1)', offset: 0 },
    { transform: 'rotate(360deg) scale(1.1)', offset: 0.8 },
    { transform: 'rotate(360deg) scale(1)', offset: 1 },
  ],
  [
    { transform: 'scaleX(1)', offset: 0 },
    { transform: 'scaleX(-1) translateY(-8px)', offset: 0.4 },
    { transform: 'scaleX(1)', offset: 1 },
  ],
  [
    { transform: 'rotate(0deg)', offset: 0 },
    { transform: 'rotate(-15deg) scale(1.15)', offset: 0.15 },
    { transform: 'rotate(12deg)', offset: 0.3 },
    { transform: 'rotate(-10deg)', offset: 0.5 },
    { transform: 'rotate(6deg)', offset: 0.7 },
    { transform: 'rotate(0deg) scale(1)', offset: 1 },
  ],
  [
    { transform: 'scale(1)', offset: 0 },
    { transform: 'scale(1.6)', offset: 0.25 },
    { transform: 'scale(0.7)', offset: 0.55 },
    { transform: 'scale(1.1)', offset: 0.8 },
    { transform: 'scale(1)', offset: 1 },
  ],
  [
    { transform: 'translateY(0) rotate(0deg)', offset: 0 },
    { transform: 'translateY(-22px) rotate(180deg) scale(1.2)', offset: 0.4 },
    { transform: 'translateY(0) rotate(360deg) scale(1)', offset: 1 },
  ],
] as const;

const SCORE_DISPLAY_MS = 1_800;
const TRAIL_COUNT = 3;
const TRAIL_SAMPLE_MS = 50;

export function ThemeMascot({ scale = 1 }: { scale?: number }) {
  const { themeDefinition, reducedMotion } = usePlayful();
  const colors = useThemeColors();
  const {
    elRef,
    reverseDirection,
    velocityRef,
    speedRef,
    lastBounceRef,
    fleeStrengthRef,
  } = usePatrolAnimation();
  const animatingRef = useRef(false);
  const mascotClickedRef = useRef(false);

  // Transform pipeline refs
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const smoothAngleRef = useRef(0);

  // Trail refs
  const trailRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const trailPositions = useRef<Vec2[]>(
    Array.from({ length: TRAIL_COUNT }, () => ({ x: 0, y: 0 }))
  );
  const lastTrailSample = useRef(0);
  const [score, setScore] = useState(0);
  const [showScore, setShowScore] = useState(false);
  const scoreTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [nearTop, setNearTop] = useState(false);

  // Reset score on any non-mascot click
  useEffect(() => {
    const onDocumentClick = () => {
      if (mascotClickedRef.current) {
        mascotClickedRef.current = false;
        return;
      }
      setScore(0);
    };
    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      mascotClickedRef.current = true;

      const el = elRef.current;
      if (!el || animatingRef.current) return;

      // Check if near top for score positioning
      const rect = el.getBoundingClientRect();
      setNearTop(rect.top < 50);

      setScore(prev => {
        const next = prev + 1;
        if (next % 7 === 0) reverseDirection();
        return next;
      });

      setShowScore(true);
      clearTimeout(scoreTimerRef.current);
      scoreTimerRef.current = setTimeout(
        () => setShowScore(false),
        SCORE_DISPLAY_MS
      );

      animatingRef.current = true;
      const reaction = REACTIONS[Math.floor(Math.random() * REACTIONS.length)];
      const keyframes = reaction.map(kf => ({
        transform: kf.transform,
        offset: kf.offset,
      }));

      const anim = el.animate(keyframes, {
        duration: 500 + Math.random() * 200,
        easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
        fill: 'none',
      });

      anim.onfinish = () => {
        animatingRef.current = false;
      };
      anim.oncancel = () => {
        animatingRef.current = false;
      };
    },
    [reverseDirection, elRef]
  );

  useEffect(() => {
    return () => clearTimeout(scoreTimerRef.current);
  }, []);

  // Card completion celebration — react in place with random stagger
  useEffect(() => {
    if (reducedMotion) return;

    const handleCardCompleted = () => {
      const stagger = Math.random() * 150;
      setTimeout(() => {
        const el = elRef.current;
        if (!el) return;

        const reaction =
          REACTIONS[Math.floor(Math.random() * REACTIONS.length)];
        const keyframes = reaction.map(kf => ({
          transform: kf.transform,
          offset: kf.offset,
        }));

        animatingRef.current = true;
        const anim = el.animate(keyframes, {
          duration: 500 + Math.random() * 200,
          easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
          fill: 'none',
        });

        anim.onfinish = () => {
          animatingRef.current = false;
        };
        anim.oncancel = () => {
          animatingRef.current = false;
        };
      }, stagger);
    };

    window.addEventListener('card-completed', handleCardCompleted);
    return () =>
      window.removeEventListener('card-completed', handleCardCompleted);
  }, [reducedMotion, elRef]);

  // Transform pipeline + trail update loop
  useEffect(() => {
    if (reducedMotion) return;
    let raf = 0;

    const updateTransforms = () => {
      raf = requestAnimationFrame(updateTransforms);
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      const vel = velocityRef.current;
      const speed = speedRef.current;
      const now = performance.now();
      const bounceDt = now - lastBounceRef.current;

      // 1. Directional tilt: lerp toward target angle
      const targetAngle = Math.atan2(vel.y, vel.x) * (180 / Math.PI);
      const clampedAngle = Math.max(-20, Math.min(20, targetAngle * 0.4));
      smoothAngleRef.current += (clampedAngle - smoothAngleRef.current) * 0.08;

      // 2. Speed stretch
      const stretchX = 1 + speed * 0.12;
      const stretchY = 1 - speed * 0.06;

      // 3. Bounce squash (decays over 300ms)
      let squashX = 1,
        squashY = 1;
      if (bounceDt < 300) {
        const t = bounceDt / 300;
        const ease = 1 - t * t; // quadratic ease out
        squashX = 1 + 0.25 * ease;
        squashY = 1 - 0.2 * ease;
      }

      const finalScaleX = stretchX * squashX;
      const finalScaleY = stretchY * squashY;
      const finalAngle = smoothAngleRef.current;

      // 4. Scared shrink: cower when mouse is nearby
      const fleeSt = fleeStrengthRef.current;
      const scaredScale = fleeSt > 0 ? 1 - fleeSt * 0.2 : 1;

      wrapper.style.transform = `rotate(${finalAngle.toFixed(1)}deg) scaleX(${(finalScaleX * scaredScale).toFixed(3)}) scaleY(${(finalScaleY * scaredScale).toFixed(3)})`;

      // Trail position sampling
      const trailNow = performance.now();
      if (
        trailNow - lastTrailSample.current > TRAIL_SAMPLE_MS &&
        elRef.current
      ) {
        lastTrailSample.current = trailNow;
        for (let i = TRAIL_COUNT - 1; i > 0; i--) {
          trailPositions.current[i] = { ...trailPositions.current[i - 1] };
        }
        trailPositions.current[0] = {
          x: parseFloat(elRef.current.style.left) || 0,
          y: parseFloat(elRef.current.style.top) || 0,
        };
      }

      // Trail DOM update
      for (let i = 0; i < TRAIL_COUNT; i++) {
        const trail = trailRefs.current[i];
        if (!trail) continue;
        const pos = trailPositions.current[i];
        trail.style.left = `${pos.x}px`;
        trail.style.top = `${pos.y}px`;
        trail.style.opacity = speed > 0.3 ? `${0.25 - i * 0.07}` : '0';
      }
    };

    raf = requestAnimationFrame(updateTransforms);
    return () => cancelAnimationFrame(raf);
  }, [reducedMotion, velocityRef, speedRef, lastBounceRef, elRef]);

  if (reducedMotion || !themeDefinition.metadata.mascotEmoji) {
    return null;
  }

  const { idle } = themeDefinition;
  const scorePosition = nearTop
    ? { bottom: '-1.4em' as const, top: undefined as undefined }
    : { top: '-1.4em' as const, bottom: undefined as undefined };

  return (
    <>
      {/* Trailing ghost copies (rendered behind the main mascot) */}
      {Array.from({ length: TRAIL_COUNT }, (_, i) => (
        <span
          key={`trail-${i}`}
          ref={el => {
            trailRefs.current[i] = el;
          }}
          aria-hidden
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            pointerEvents: 'none',
            display: 'inline-block',
            fontSize: `calc(${typography.sizes.xl} * ${scale})`,
            lineHeight: typography.lineHeights.tight,
            zIndex: 24,
            opacity: 0,
            filter: `blur(${1 + i}px)`,
            transform: `scale(${0.8 - i * 0.1})`,
            willChange: 'left, top, opacity',
            userSelect: 'none',
          }}
        >
          {(() => {
            const SvgMascot = MASCOT_COMPONENTS[themeDefinition.id];
            return SvgMascot ? (
              <SvgMascot />
            ) : (
              themeDefinition.metadata.mascotEmoji
            );
          })()}
        </span>
      ))}

      {/* Main mascot — outer span handles position, inner wrapper handles transforms */}
      <span
        ref={elRef}
        role="img"
        aria-label={`${themeDefinition.metadata.label} mascot`}
        onClick={handleClick}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          pointerEvents: 'auto',
          cursor: 'pointer',
          display: 'inline-block',
          fontSize: `calc(${typography.sizes.xl} * ${scale})`,
          lineHeight: typography.lineHeights.tight,
          zIndex: 25,
          willChange: 'left, top',
          userSelect: 'none',
        }}
      >
        <span
          ref={wrapperRef}
          style={{
            display: 'inline-block',
            animationName: idle.animationName,
            animationDuration: idle.animationDuration,
            animationTimingFunction: idle.animationTimingFunction,
            animationIterationCount: idle.animationIterationCount,
          }}
        >
          {(() => {
            const SvgMascot = MASCOT_COMPONENTS[themeDefinition.id];
            return SvgMascot ? (
              <SvgMascot />
            ) : (
              themeDefinition.metadata.mascotEmoji
            );
          })()}
        </span>

        {showScore && score > 0 && (
          <span
            style={{
              position: 'absolute',
              ...scorePosition,
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '0.5em',
              fontWeight: 700,
              lineHeight: typography.lineHeights.tight,
              color: colors.text.secondary,
              textShadow: `0 0 4px ${hexToRgba(colors.background, 0.9)}`,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              animation:
                'mascot-score-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            {score}
          </span>
        )}
      </span>
    </>
  );
}
